/**
 * Screen 14c — members and role assignment.
 *
 * Role checkboxes are only rendered for a volunteer who can actually assign
 * them (`OWNER`). A `MEMBERSHIP` volunteer sees the roles a member holds —
 * useful when someone rings up asking why they cannot open the scanner — but
 * no controls, because being able to grant `FINANCE` means being able to
 * approve your own refunds.
 *
 * `OWNER` is shown with what it expands to, so nobody has to remember that
 * granting it implies the other four rather than sitting alongside them.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorNote, Note, Shell } from "../../components/Shell";
import { ApiError, api, type AdminMember, type Me } from "../../lib/api";

const ROLES = ["DOOR", "PROGRAM", "MEMBERSHIP", "FINANCE", "OWNER"] as const;

const ROLE_HINT: Record<string, string> = {
  DOOR: "admin.role_door",
  PROGRAM: "admin.role_program",
  MEMBERSHIP: "admin.role_membership",
  FINANCE: "admin.role_finance",
  OWNER: "admin.role_owner",
};

export default function AdminMembers() {
  const { t } = useTranslation();
  const [me, setMe] = useState<Me | null>(null);
  const [members, setMembers] = useState<AdminMember[] | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (q: string) =>
      api.admin
        .members(q)
        .then((body) => setMembers(body.members))
        .catch(() => setError(t("error.generic"))),
    [t],
  );

  useEffect(() => {
    api.me().then(setMe).catch(() => undefined);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => void load(query), 250);
    return () => clearTimeout(timer);
  }, [query, load]);

  const mayAssign = Boolean(me?.capabilities.assign_roles);

  async function toggle(member: AdminMember, role: string, on: boolean) {
    setError(null);
    try {
      if (on) await api.admin.grantRole(member.id, role);
      else await api.admin.revokeRole(member.id, role);
      await load(query);
    } catch (err) {
      // "This is the last OWNER" is written for a person to read.
      setError(err instanceof ApiError ? err.message : t("error.generic"));
    }
  }

  async function setStatus(member: AdminMember, status: string) {
    setError(null);
    try {
      await api.admin.updateMember(member.id, { status });
      await load(query);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("error.generic"));
    }
  }

  return (
    <Shell area="membership" title={t("admin.members")}>
      <input
        className="field"
        type="search"
        value={query}
        placeholder={t("common.search")}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={t("common.search")}
      />

      {error && <ErrorNote>{error}</ErrorNote>}
      {members === null && !error && <Note>{t("common.loading")}</Note>}
      {members?.length === 0 && <Note>{t("admin.no_members")}</Note>}

      <ul className="cards">
        {(members ?? []).map((member) => (
          <li key={member.id} className="card">
            <h3 className="card__title">{member.name}</h3>
            <p className="card__meta">
              {member.email ?? "—"}
              {member.member_no && ` · ${member.member_no}`}
            </p>
            <p>
              <span className="state" data-state={member.status === "ACTIVE" ? "PAID" : "HELD"}>
                {member.status}
              </span>
            </p>

            {mayAssign ? (
              <div className="stack">
                {ROLES.map((role) => {
                  const has = member.roles.includes(role);
                  return (
                    <label key={role} className="rolerow">
                      <input
                        type="checkbox"
                        checked={has}
                        onChange={(e) => void toggle(member, role, e.target.checked)}
                      />
                      <span>
                        <strong>{role}</strong>
                        <br />
                        <span className="fallback-note">{t(ROLE_HINT[role])}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="fallback-note">{member.roles.join(" · ") || t("admin.no_roles")}</p>
            )}

            {mayAssign && (
              <select
                className="field"
                value={member.status}
                onChange={(e) => void setStatus(member, e.target.value)}
                aria-label={t("admin.status")}
              >
                {["PENDING", "ACTIVE", "EXPIRED", "SUSPENDED"].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
          </li>
        ))}
      </ul>

      <Link className="btn" to="/admin">
        {t("common.back")}
      </Link>
    </Shell>
  );
}
